import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  TokenLoginView,
  type TokenLoginStatus,
} from "@components/feature/login/TokenLoginView";
import { QUERY_BASE_KEY } from "@constants/query-key";
import "@css/tokenLogin.css";
import * as auth from "@script/auth";
import {
  readDemoSsoContext,
  saveDemoSsoContext,
} from "@script/demo-sso-session";
import useStore from "@store/store";

import { consumeTokenLoginMemberId } from "./tokenLoginLocation";

/**
 * 서버 로그아웃 대기 상한.
 *
 * ★ `auth.logout()` 에는 타임아웃이 없다. 서버가 연결만 받고 응답하지 않으면
 *   여기서 영원히 기다리게 되고 **로그인 자체가 시작되지 못한다**.
 * ★★ 상한을 넘기면 **요청 자체를 abort 한다.** 기다림만 멈추면(Promise.race)
 *   요청이 살아남아, 지연된 로그아웃 응답이 **새로 만든 세션을 무효화**한다.
 */
const LOGOUT_TIMEOUT_MS = 3000;
const TOKEN_LOGIN_TIMEOUT_MS = 8000;

export default function TokenLogin() {
  const [status, setStatus] = useState<TokenLoginStatus>("pending");
  const [unauthorizedMessage, setUnauthorizedMessage] = useState("");
  // 시연 역할 선택지 — 백엔드가 대상 계정에만 내려준다. 계정 ID 는 담기지 않는다.
  const [demoRoles, setDemoRoles] = useState<
    ReadonlyArray<{ readonly role: string; readonly label: string }>
  >([]);
  const [switchingRole, setSwitchingRole] = useState<
    string | null | undefined
  >(undefined);
  const [entryMemberId] = useState(() => {
    const queryMemberId = consumeTokenLoginMemberId();
    if (queryMemberId) return queryMemberId;

    const context = readDemoSsoContext();
    return context?.phase === "selecting" ? context.memberid : null;
  });
  // 발급받은 SSO 토큰 — 역할 선택 후 로그인에 다시 쓴다. 렌더에 관여하지 않으므로 ref.
  const ssoTokenRef = useRef<auth.MeditongToken | null>(null);
  const isDemoSsoRef = useRef(false);
  const generationRef = useRef(0);
  const activeRequestControllerRef = useRef<AbortController | null>(null);
  // ★ 로그인 진행 중 표시는 state와 별개인 **동기** 가드다.
  //   버튼을 빠르게 두 번 누르면 state 커밋 전에 로그인이 두 번 시작될 수 있다.
  //   서로 다른 역할이면 최종 세션 쿠키와 화면이 응답 순서에 따라 어긋난다.
  const loginInFlightRef = useRef(false);
  const { setLoginUser, logout, setDemoSession, setActiveDemoRole } =
    useStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * 이전 세션 잔재를 지운다 — **진입 즉시** 호출한다.
   *
   * ★ 순서가 핵심이다. `cancelQueries` 를 **await 로 기다린 뒤** 캐시를 지우고
   *   로그아웃해야 한다. 진행 중인 `/auth/me` 를 남겨 두면 그 응답이 나중에 도착해
   *   전역 `useSession` 의 `setLoginUser(data)` 가 로그인 상태를 **되살리고**,
   *   빈 캐시를 메우려 다시 fetch → 응답 → 되살림 이 끊기지 않는다
   *   (실측: `/auth/me` 수천 회 → ERR_INSUFFICIENT_RESOURCES → 화면 정지).
   *   `void cancelQueries()` 로 던지면 취소가 끝나기 전에 아래가 실행돼 같은 일이 난다.
   *
   * ★ 역할 선택 모달을 기다리는 동안에도 **반드시 정리된 상태여야 한다.**
   *   이전 계정을 남겨 두면 헤더·네비게이션이 살아 있어 모달을 벗어나
   *   직전 사용자의 데이터에 접근할 수 있다.
   *
   * ★★ 스토어의 `logout()` 은 **클라이언트 상태만** 지운다. 서버 쿠키가 살아 있으면
   *   화면상 로그아웃돼 보여도 API 요청은 여전히 이전 계정으로 인증된다.
   *   선택을 기다리는 시간이 무기한이므로 `auth.logout()` 으로 **서버 세션까지** 끊는다.
   *   (실패해도 내부에서 삼키므로 진행을 막지 않는다.)
   */
  const resetSession = async (signal?: AbortSignal): Promise<boolean> => {
    await queryClient.cancelQueries();
    queryClient.removeQueries();
    logout();

    const logoutAbort = new AbortController();
    const abortFromCaller = () => logoutAbort.abort();
    if (signal?.aborted) {
      logoutAbort.abort();
    } else {
      signal?.addEventListener("abort", abortFromCaller, { once: true });
    }
    const logoutTimer = setTimeout(
      () => logoutAbort.abort(),
      LOGOUT_TIMEOUT_MS
    );
    let loggedOut = false;
    try {
      loggedOut = await auth.logout(logoutAbort.signal);
    } finally {
      clearTimeout(logoutTimer);
      signal?.removeEventListener("abort", abortFromCaller);
    }
    // ★ 정리를 **보장하지 못한 모든 경우**를 실패로 본다 — 타임아웃(abort)뿐 아니라
    //   네트워크·CORS·비정상 응답도 포함이다. 그대로 로그인하면 이전 세션이 살아 있거나,
    //   늦게 도착한 로그아웃 응답이 새 세션의 쿠키를 지울 수 있다.
    return loggedOut && !logoutAbort.signal.aborted;
  };

  /** 토큰(+선택한 역할)으로 실제 로그인하고 홈으로 보낸다. */
  const runLogin = async (token: auth.MeditongToken, demoRole?: string) => {
    const generation = generationRef.current;
    activeRequestControllerRef.current?.abort();
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    const timer = window.setTimeout(
      () => controller.abort(),
      TOKEN_LOGIN_TIMEOUT_MS
    );
    try {
      await auth.tokenlogin(token, demoRole, controller.signal);
      const loginUser = await auth.loadLoginUser(controller.signal);
      if (!loginUser) {
        throw new auth.AuthError({
          code: "SESSION_LOAD_FAILED",
          message: "로그인 사용자 정보를 불러올 수 없습니다.",
        });
      }
      if (generationRef.current !== generation) return;
      if (controller.signal.aborted) {
        throw new auth.AuthError({
          code: "SESSION_LOAD_FAILED",
          message: "토큰 로그인 요청 시간이 초과되었습니다.",
        });
      }

      queryClient.setQueryData([QUERY_BASE_KEY.SESSION], loginUser);
      setLoginUser(loginUser);
      setActiveDemoRole(demoRole ?? null);
      if (isDemoSsoRef.current) {
        saveDemoSsoContext({
          phase: "authenticated",
          memberid: token.memberid,
          activeRole: demoRole ?? null,
        });
      }
      navigate("/");
    } catch (error) {
      // 실패했으니 다시 고를 수 있도록 가드를 푼다.
      loginInFlightRef.current = false;
      if (generationRef.current !== generation) return;
      await resetSession();
      if (generationRef.current !== generation) return;
      if (
        error instanceof auth.AuthError &&
        error.code === "ROSTER_NOT_ELIGIBLE"
      ) {
        setUnauthorizedMessage(error.message);
        setStatus("unauthorized");
        return;
      }
      setStatus("error");
    } finally {
      window.clearTimeout(timer);
      if (generationRef.current === generation) {
        setSwitchingRole(undefined);
      }
      if (activeRequestControllerRef.current === controller) {
        activeRequestControllerRef.current = null;
      }
    }
  };

  /** role 을 생략하면 스왑 없이 **본인 계정**으로 로그인한다. */
  const handleSelectRole = (role?: string) => {
    if (loginInFlightRef.current) return;
    const token = ssoTokenRef.current;
    if (!token) {
      // 토큰 없이 여기 오면 로그인은 불가능하다 = 종료 경로이므로 세션을 정리한다.
      void resetSession();
      setStatus("error");
      return;
    }
    loginInFlightRef.current = true;
    setSwitchingRole(role ?? null);
    void runLogin(token, role);
  };

  useEffect(() => {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    const controller = new AbortController();
    activeRequestControllerRef.current = controller;
    const isCurrent = () =>
      generationRef.current === generation && !controller.signal.aborted;

    const memberId = entryMemberId;

    const authenticate = async () => {
      // ★ 이전 세션을 **먼저, 끝까지** 정리한다. 역할 선택을 기다리는 동안에도
      //   직전 계정이 살아 있으면 안 된다. 정리를 보장하지 못하면 진입하지 않는다.
      const cleared = await resetSession(controller.signal);
      if (!isCurrent()) return;
      if (!cleared) {
        console.error(
          "[TokenLogin] 이전 세션 정리 시간 초과 — 진입을 중단한다"
        );
        setStatus("error");
        return;
      }

      if (!memberId) {
        setStatus("error");
        return;
      }

      let token: auth.MeditongToken;
      try {
        token = await auth.fetchTokenFromMeditong(memberId, controller.signal);
      } catch (error) {
        if (!isCurrent()) return;
        if (!(error instanceof auth.AuthError)) throw error;
        setStatus("error");
        return;
      }
      if (!isCurrent()) return;
      ssoTokenRef.current = token;

      // 내부(메디통) 직원이면 역할 선택 모달을 먼저 띄운다.
      // 조회가 실패하면 fetchSsoMode 가 normal 을 돌려주므로 정상 진입이 막히지 않는다.
      const mode = await auth.fetchSsoMode(token, controller.signal);
      if (!isCurrent()) return;
      if (mode.mode === "demo" && mode.roles.length > 0) {
        isDemoSsoRef.current = true;
        saveDemoSsoContext({
          phase: "selecting",
          memberid: token.memberid,
          activeRole: null,
        });
        setDemoSession({
          token: token.token,
          memberid: token.memberid,
          roles: mode.roles,
        });
        setDemoRoles(mode.roles);
        setStatus("select");
        return;
      }

      await runLogin(token);
    };

    void authenticate();
    return () => {
      if (generationRef.current === generation) {
        generationRef.current += 1;
      }
      controller.abort();
      activeRequestControllerRef.current?.abort();
      if (activeRequestControllerRef.current === controller) {
        activeRequestControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    logout,
    navigate,
    queryClient,
    setActiveDemoRole,
    setDemoSession,
    setLoginUser,
  ]);

  return (
    <TokenLoginView
      status={status}
      unauthorizedMessage={unauthorizedMessage}
      demoRoles={demoRoles}
      switchingRole={switchingRole}
      onSelectRole={handleSelectRole}
    />
  );
}
